import React, { useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import {authScopeForRoute, homeRouteForRoles, SecondFactorRequiredError, useAuth} from "../auth";
import {navigate, useRoute} from "../hooks";
import { Logo } from "../components";

export function LoginPage(): React.JSX.Element {
  const { login, sessionEnded } = useAuth();
  const route = useRoute();
  const merchantAuth = authScopeForRoute(route) === "merchant";
  const [form, setForm] = useState({ login: "", password: "" });
  // Второй шаг: пароль принят, сервер ждёт код из приложения.
  const [codeStep, setCodeStep] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    e: MouseEvent | KeyboardEvent
  ): Promise<void> => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.login, form.password, codeStep ? code : undefined);
      const destination = homeRouteForRoles(user.roles);
      if (!destination) throw new Error("Account has no application role");
      navigate(destination);
    } catch (err) {
      if (err instanceof SecondFactorRequiredError) {
        setCodeStep(true);
        setCode("");
      } else {
        setError(err instanceof Error ? err.message : "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = (): void => {
    setCodeStep(false);
    setCode("");
    setError("");
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Logo size={32} />
          </div>
          <h1>{merchantAuth ? "Merchant LK" : "Payments"}</h1>
          <p className="login-subtitle">
            {merchantAuth ? "Вход для мерчанта" : "Sign in to your account"}
          </p>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        {sessionEnded && !error && !codeStep && (
          <div className="alert alert-info">Войдите заново: прежний вход больше не действует.</div>
        )}
        {codeStep ? (
          <div className="form-group">
            <label htmlFor="login-otp">2FA</label>
            <input
              id="login-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
              placeholder="123 456"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
              autoFocus
            />
            <p className="login-subtitle">
              Шесть цифр для {form.login} из Google Authenticator, Authy или другого приложения для 2FA.
            </p>
          </div>
        ) : (
          <>
            <div className="form-group">
              <label>Login</label>
              <input
                type="text"
                value={form.login}
                onChange={(e) => setForm({ ...form, login: e.target.value })}
                placeholder="your.login"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
              />
            </div>
          </>
        )}
        <button
          className="btn btn-primary btn-full"
          onClick={handleSubmit}
          disabled={loading || (codeStep && code.replace(/\s/g, "").length !== 6)}
        >
          {loading ? "Signing in…" : codeStep ? "Войти" : "Sign In"}
        </button>
        {codeStep && (
          <button className="btn btn-ghost btn-full" type="button" onClick={backToPassword} disabled={loading}>
            Назад к паролю
          </button>
        )}
      </div>
    </div>
  );
}
